/* eslint-disable react-hooks/set-state-in-effect */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { UserBubble, BotBubble, BubbleTimestamp } from './ChatBubble';
import ChatMarkdownMessage from './ChatMarkdownMessage';
import ChatResponseButtons from './ChatResponseButtons';
import ChatPromptOptions from './ChatPromptOptions';
import ChatFooter from './ChatFooter';
import { LoadingDots } from './LoadingDots';
import OptumusLogo from '../assets/Optumus-logo-transparent.png';
import ProviderCardDetails from './ProviderCardDetails';
import type { ProviderCardDetailsProps } from './ProviderCardDetails';
import ProviderCard, { type Provider } from './ProviderCard';
import ReviewAppointment, { type ReviewAppointmentProps } from './ReviewAppointment';
import type { MessageButton } from '../types/message.types';
import { useContentConfig } from '../hooks';
import { useStateCopy } from '../hooks/useStateCopy';
import { CalendarModal } from './calender/CalendarModal';
import { DynamicFormModal } from './DynamicFormModal';
import { useChatApi } from '../../../optumus-core/src/hooks/useChatApi';
import type { IChatSchedulingResponse } from '../../../optumus-core/src/types/ChatSchedulingResponse.types';
import {
  isChatAgentResponse,
  type IAppointmentSlotActionData,
  type ICaptureUserDetailsActionData,
  type IChatAgentResponse,
  type IProviderResultsActionData,
  type IReviewAppointmentActionData,
} from '../../../optumus-core/src/types/ChatAgentResponse.types';
import type { ICalendarSlot } from './calender/CalendarModal.types';
import {
  mapCaptureUserDetailsToDynamicFormConfig,
  type IChatCopyData,
  mapAppointmentDetailsToProps,
} from '../../../optumus-core/src/transformers/chatActionTransformers';

type UserFormData = Partial<Record<string, string>>;

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  buttons?: MessageButton[];
  buttonsDisabled?: boolean;
  providerCard?: ProviderCardDetailsProps;
  providerSearchResults?: Provider[];
  isStreaming?: boolean;
  reviewAppointmentCard?: ReviewAppointmentProps;
}

interface ChatMessageListProps {
  initialPrompt?: string;
  selectedProvider?: ProviderCardDetailsProps;
  onProviderResults?: (response: IChatSchedulingResponse) => void;
}

const mapAppointmentSlotsToCalendarSlots = (
  actionData: IAppointmentSlotActionData,
): ICalendarSlot[] => {
  return actionData.slots
    .filter((slot) => slot.available)
    .map((slot, index) => {
      const numericId = Number(slot.slotId.replace(/\D/g, ''));
      const appointmentId = Number.isFinite(numericId) ? numericId : index + 1;

      return {
        dateTime: `${slot.date}T${slot.time}:00`,
        duration: slot.duration,
        appointmentType: slot.visitType,
        appointmentId,
        appointmentTypeId: 0,
        departmentId: 0,
        deptName: actionData.providerName,
        deptAddress: '',
      };
    });
};

const mapProviderResultsToProviders = (
  actionData: IProviderResultsActionData | undefined,
): Provider[] => actionData?.providerResults ?? [];

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  initialPrompt,
  selectedProvider,
  onProviderResults,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarSlots, setCalendarSlots] = useState<ICalendarSlot[]>([]);
  const [showUserCaptureForm, setShowUserCaptureForm] = useState(false);
  const [captureUserDetailsActionData, setCaptureUserDetailsActionData] =
    useState<ICaptureUserDetailsActionData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: config } = useContentConfig();
  const chatContent = config?.content;
  const featureFlags = config?.featureFlags || { showTimestamps: false };
  const { data: StateNamesCopy } = useStateCopy();
  const { sendMessage, isStreaming, error, streamedText, latestJsonResponse, resetStreamedText } =
    useChatApi();
  const streamingMessageIdRef = useRef<string | null>(null);
  const pendingButtonsRef = useRef<{
    messageId: string;
    buttons: MessageButton[];
    otherData: {
      providerSearchResults: Provider[];
      reviewAppointmentCard: ReturnType<typeof mapAppointmentDetailsToProps>;
    };
  } | null>(null);

  const scrollToEnd = useCallback(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  const handleDynamicFormSave = useCallback(
    (formData: UserFormData) => {
      setShowUserCaptureForm(false);
      setCaptureUserDetailsActionData(null);

      // Convert form data to comma-separated values
      const userDetailsText = Object.values(formData)
        .filter((value) => value)
        .join(', ');

      if (!userDetailsText.trim()) {
        return;
      }

      const userMessage: Message = {
        id: Date.now().toString(),
        text: userDetailsText,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      scrollToEnd();

      const botMessageId = (Date.now() + 1).toString();
      streamingMessageIdRef.current = botMessageId;

      const botMessage: Message = {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();

      sendMessage(userDetailsText);
    },
    [scrollToEnd, sendMessage],
  );

  const handleCloseDynamicFormModal = useCallback(() => {
    setShowUserCaptureForm(false);
    setCaptureUserDetailsActionData(null);
  }, []);

  const stateOptions = StateNamesCopy?.stateList?.map(
    (state: { name: string; abbreviation: string }) => ({
      label: state.name,
      value: state.abbreviation,
    }),
  );

  const dynamicFormConfig = captureUserDetailsActionData
    ? mapCaptureUserDetailsToDynamicFormConfig(
      captureUserDetailsActionData,
      chatContent as IChatCopyData,
      {
        state: { items: stateOptions },
      },
    )
    : undefined;

  const handleButtonPress = useCallback(
    (button: MessageButton, messageId: string) => {
      if (button.actionId === 'appointmentSlots') {
        const data = button.data as IAppointmentSlotActionData | undefined;

        if (data?.slots?.length) {
          setCalendarSlots(mapAppointmentSlotsToCalendarSlots(data));
          setIsCalendarOpen(true);
        }

        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, buttonsDisabled: true } : msg)),
        );

        return;
      }

      if (button.actionId === 'providerResults') {
        const providerSearchResults = mapProviderResultsToProviders(
          button.data as IProviderResultsActionData | undefined,
        );
        if (onProviderResults) {
          onProviderResults(latestJsonResponse as unknown as IChatSchedulingResponse);
          setMessages((prev) =>
            prev.map((msg) => (msg.id === messageId ? { ...msg, buttonsDisabled: true } : msg)),
          );
          return;
        } else {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId ? { ...msg, buttonsDisabled: true, providerSearchResults } : msg,
            ),
          );

          scrollToEnd();
          return;
        }
      }

      if (button.actionId === 'followUpQuery') {
        const queryText = typeof button.data?.queryText === 'string' ? button.data.queryText : '';

        if (queryText) {
          setMessages((prev) =>
            prev.map((msg) => (msg.id === messageId ? { ...msg, buttonsDisabled: true } : msg)),
          );

          const userMessage: Message = {
            id: Date.now().toString(),
            text: queryText,
            sender: 'user',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, userMessage]);
          scrollToEnd();

          const botMessageId = (Date.now() + 1).toString();
          streamingMessageIdRef.current = botMessageId;

          const botMessage: Message = {
            id: botMessageId,
            text: '',
            sender: 'bot',
            timestamp: new Date(),
            isStreaming: true,
          };
          setMessages((prev) => [...prev, botMessage]);
          scrollToEnd();

          sendMessage(queryText);
        }

        return;
      }

      if (button.actionId === 'captureUserDetails') {
        const data = button.data as ICaptureUserDetailsActionData | undefined;

        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, buttonsDisabled: true } : msg)),
        );

        if (data?.fields?.length) {
          setCaptureUserDetailsActionData(data);
          setShowUserCaptureForm(true);
        }

        return;
      }

      if (button.responseKey === 'navigate_findCare') {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, buttonsDisabled: true } : msg)),
        );
        return;
      }

      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, buttonsDisabled: true } : msg)),
      );

      const userMessage: Message = {
        id: Date.now().toString(),
        text: button.text,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      scrollToEnd();
    },
    [scrollToEnd, latestJsonResponse, onProviderResults, sendMessage],
  );

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        return;
      }

      setHasStartedChat(true);
      resetStreamedText();

      setMessages((prev) =>
        prev.map((msg) =>
          msg.buttons && !msg.buttonsDisabled ? { ...msg, buttonsDisabled: true } : msg,
        ),
      );

      const userMessage: Message = {
        id: Date.now().toString(),
        text: trimmed,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      scrollToEnd();

      const botMessageId = (Date.now() + 1).toString();
      streamingMessageIdRef.current = botMessageId;

      const botMessage: Message = {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();

      sendMessage(trimmed);
    },
    [scrollToEnd, sendMessage, resetStreamedText],
  );

  const handlePromptPress = useCallback(
    (identifier: string, title: string) => {
      if (identifier === 'required_login') {
        window.alert(`Login Required\nPlease log in to access your health summary.`);
        return;
      }

      setHasStartedChat(true);
      resetStreamedText();

      const userMessage: Message = {
        id: Date.now().toString(),
        text: title,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      scrollToEnd();

      const botMessageId = (Date.now() + 1).toString();
      streamingMessageIdRef.current = botMessageId;

      const botMessage: Message = {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();

      sendMessage(title);
    },
    [scrollToEnd, sendMessage, resetStreamedText],
  );

  const handleProviderSchedulePress = useCallback(
    (provider: Provider) => {
      const providerDisplayName = provider.credentials
        ? `${provider.providerName}, ${provider.credentials}`
        : provider.providerName;

      handleSend(`I would like to schedule with ${providerDisplayName}.`);
    },
    [handleSend],
  );

  const handleProviderDirectionsPress = useCallback((provider: Provider) => {
    const encodedAddress = encodeURIComponent(provider.address);
    const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;

    try {
      window.open(directionsUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening maps:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      const requestId = Date.now().toString();
      const providerName = selectedProvider.education
        ? `${selectedProvider.firstName} ${selectedProvider.lastName}, ${selectedProvider.education}`
        : `${selectedProvider.firstName} ${selectedProvider.lastName}`;
      const schedulingMessage = `${chatContent?.providerSchedulingPrefix}${providerName}`;

      setHasStartedChat(true);
      resetStreamedText();

      setMessages((prev) => [
        ...prev,
        {
          id: `provider-scheduling-user-${requestId}`,
          text: schedulingMessage,
          sender: 'user',
          timestamp: new Date(),
        },
        {
          id: `provider-card-initial-${requestId}`,
          text: '',
          sender: 'bot',
          timestamp: new Date(),
          providerCard: selectedProvider,
        },
      ]);
      scrollToEnd();

      const botMessageId = `provider-scheduling-bot-stream-${requestId}`;
      streamingMessageIdRef.current = botMessageId;
      setMessages((prev) => [
        ...prev,
        {
          id: botMessageId,
          text: '',
          sender: 'bot',
          timestamp: new Date(),
          isStreaming: true,
        },
      ]);
      scrollToEnd();

      sendMessage(schedulingMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  useEffect(() => {
    if (initialPrompt) {
      handleSend(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  useEffect(() => {
    // Don't process if there's no active streaming message
    if (!streamingMessageIdRef.current) {
      return;
    }

    const currentId = streamingMessageIdRef.current;

    // Only update during active streaming with text
    if (isStreaming || (streamedText && !latestJsonResponse)) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentId ? { ...msg, text: streamedText, isStreaming } : msg,
        ),
      );
      scrollToEnd();
    }
  }, [streamedText, isStreaming, latestJsonResponse, scrollToEnd]);

  // Reveal the pending action buttons once the message's text has finished
  // animating on screen (called from StreamingText's onComplete via the message).
  const revealPendingButtons = useCallback(
    (messageId: string) => {
      const pending = pendingButtonsRef.current;
      if (!pending || pending.messageId !== messageId) {
        return;
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? {
              ...msg,
              buttons: pending.buttons,
              buttonsDisabled: false,
              providerSearchResults: pending.otherData.providerSearchResults,
              reviewAppointmentCard: pending.otherData.reviewAppointmentCard,
            }
            : msg,
        ),
      );
      pendingButtonsRef.current = null;
      scrollToEnd();
    },
    [scrollToEnd],
  );

  useEffect(() => {
    if (!latestJsonResponse) {
      return;
    }

    if (!isChatAgentResponse(latestJsonResponse)) {
      return;
    }

    const response = latestJsonResponse as IChatAgentResponse;
    const messageId = streamingMessageIdRef.current ?? Date.now().toString();

    const providerResultsAction = response.userActions.find(
      (action) => action.actionId === 'providerResults',
    );
    const providerSearchResults = onProviderResults
      ? []
      : mapProviderResultsToProviders(
        providerResultsAction?.data as IProviderResultsActionData | undefined,
      );

    const actionButtons: MessageButton[] = response.userActions
      .filter(
        (action) =>
          (action.actionId !== 'providerResults' || Boolean(onProviderResults)) &&
          action.actionId !== 'reviewAppointment',
      )
      .map((action, index) => ({
        id: `${messageId}-${index}-${action.actionId}`,
        text: action.actionButtonTitle,
        responseKey: 'json_action',
        actionId: action.actionId,
        data: action.data,
      }));

    const reviewAppointmentProps = mapAppointmentDetailsToProps(
      response.userActions.find((action) => action.actionId === 'reviewAppointment')?.data as
      | IReviewAppointmentActionData
      | undefined,
    );

    // Store buttons in ref to be applied after streaming completes
    pendingButtonsRef.current = {
      messageId,
      buttons: actionButtons,
      otherData: {
        providerSearchResults,
        reviewAppointmentCard: reviewAppointmentProps,
      },
    };

    // Update message with final text, mark streaming as complete
    setMessages((prev) => {
      return prev.map((msg) =>
        msg.id === messageId
          ? {
            ...msg,
            text: response.textResponse,
            isStreaming: false,
          }
          : msg,
      );
    });

    // Clear the streaming message ref after processing JSON response
    streamingMessageIdRef.current = null;

    // If there's no text to animate, reveal the buttons right away; otherwise
    // they are revealed once StreamingText finishes animating the full text.
    if (!response.textResponse || response.textResponse.trim().length === 0) {
      revealPendingButtons(messageId);
    }

    scrollToEnd();
  }, [latestJsonResponse, onProviderResults, scrollToEnd, revealPendingButtons]);

  useEffect(() => {
    if (error && streamingMessageIdRef.current) {
      const currentId = streamingMessageIdRef.current;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentId
            ? {
              ...msg,
              text: 'Sorry, something went wrong. Please try again.',
              isStreaming: false,
            }
            : msg,
        ),
      );
      streamingMessageIdRef.current = null;
    }
  }, [error]);

  const formatTimestamp = React.useCallback((date: Date): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${displayHours}:${displayMinutes} ${ampm}`;
  }, []);

  const renderMessage = useCallback(
    ({ item, showLoader }: { item: Message; showLoader?: boolean }) => {
      const isUser = item.sender === 'user';
      const isStreamingPlaceholder =
        item.sender === 'bot' && Boolean(item.isStreaming) && item.text.trim().length === 0;
      const hasBotMessage = item.isStreaming || item.text.trim().length > 0;

      return (
        <MessageRow>
          {isUser ? (
            <UserMessageWrapper>
              <UserBubble>{item.text}</UserBubble>
            </UserMessageWrapper>
          ) : (
            <BotMessageContainer>
              {isStreamingPlaceholder && showLoader ? (
                <BotRow>
                  <IconWrapper>
                    <FeatureCardIconWrapper bg="$core.color.neutral.0">
                      <img
                        src={OptumusLogo}
                        alt=""
                        style={{ width: 24, height: 24, objectFit: 'contain' }}
                      />
                    </FeatureCardIconWrapper>
                  </IconWrapper>
                  <BotContentWrapper>
                    <BotBubble>
                      <LoadingDots />
                      {featureFlags.showTimestamps && (
                        <BubbleTimestamp>{formatTimestamp(item.timestamp)}</BubbleTimestamp>
                      )}
                    </BotBubble>
                  </BotContentWrapper>
                </BotRow>
              ) : hasBotMessage ? (
                <BotRow>
                  <IconWrapper>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M9.65306 6.67742C10.0923 6.67742 10.4822 6.95877 10.6211 7.3759L12.1179 11.8712L16.6084 13.3696C17.0251 13.5087 17.3061 13.899 17.3061 14.3387C17.3061 14.7784 17.0251 15.1688 16.6084 15.3078L12.1179 16.8062L10.6211 21.3015C10.4822 21.7186 10.0923 22 9.65306 22C9.21385 22 8.82391 21.7186 8.68502 21.3015L7.1882 16.8062L2.69773 15.3078C2.28105 15.1688 2 14.7784 2 14.3387C2 13.899 2.28105 13.5087 2.69773 13.3696L7.1882 11.8712L8.68502 7.3759C8.82391 6.95877 9.21385 6.67742 9.65306 6.67742Z"
                        fill="#F5B700"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M17.8163 2C18.0799 2 18.3138 2.16881 18.3972 2.41909L19.1932 4.80981L21.5814 5.60672C21.8314 5.69015 22 5.92436 22 6.18817C22 6.45198 21.8314 6.6862 21.5814 6.76962L19.1932 7.56653L18.3972 9.95726C18.3138 10.2075 18.0799 10.3763 17.8163 10.3763C17.5528 10.3763 17.3188 10.2075 17.2355 9.95726L16.4394 7.56653L14.0513 6.76962C13.8013 6.6862 13.6327 6.45198 13.6327 6.18817C13.6327 5.92436 13.8013 5.69015 14.0513 5.60672L16.4394 4.80981L17.2355 2.41909C17.3188 2.16881 17.5528 2 17.8163 2Z"
                        fill="#F5B700"
                      />
                    </svg>
                  </IconWrapper>
                  <BotContentWrapper>
                    <BotBubble>
                      <ChatMarkdownMessage
                        content={item.text}
                        isStreaming={item.isStreaming}
                        onStreamingComplete={() => revealPendingButtons(item.id)}
                      />
                      {featureFlags.showTimestamps && (
                        <BubbleTimestamp>{formatTimestamp(item.timestamp)}</BubbleTimestamp>
                      )}
                    </BotBubble>
                  </BotContentWrapper>
                </BotRow>
              ) : null}
              {item.reviewAppointmentCard ? (
                <ReviewAppointmentWrapper>
                  <ReviewAppointment {...item.reviewAppointmentCard} />
                </ReviewAppointmentWrapper>
              ) : item.providerCard ? (
                <ProviderCardWrapper>
                  <ProviderCardDetails {...item.providerCard} />
                </ProviderCardWrapper>
              ) : item.providerSearchResults?.length ? (
                <ProviderSearchResultsWrapper>
                  <div>
                    {item.providerSearchResults.map((provider: Provider, index: number) => (
                      <ProviderCard
                        key={provider.npi || `provider-result-${index}`}
                        item={provider}
                        onScheduleButtonPress={() => handleProviderSchedulePress(provider)}
                        onDirectionsButtonPress={() => handleProviderDirectionsPress(provider)}
                      />
                    ))}
                  </div>
                </ProviderSearchResultsWrapper>
              ) : null}
              {item.buttons?.length ? (
                <ButtonsWrapper>
                  <ChatResponseButtons
                    buttons={item.buttons}
                    disabled={item.buttonsDisabled ?? false}
                    onButtonPress={(button) => handleButtonPress(button, item.id)}
                  />
                </ButtonsWrapper>
              ) : null}
            </BotMessageContainer>
          )}
        </MessageRow>
      );
    },
    [
      handleButtonPress,
      handleProviderDirectionsPress,
      handleProviderSchedulePress,
      featureFlags.showTimestamps,
      formatTimestamp,
      revealPendingButtons,
    ],
  );

  const handleSlotSelection = useCallback(
    (slot: ICalendarSlot) => {
      setIsCalendarOpen(false);
      resetStreamedText();

      const requestId = Date.now().toString();
      const queryString = `Please proceed for appointment booking on ${new Date(slot.dateTime).toLocaleString()} for ${slot.appointmentType}.`;

      const userMessage: Message = {
        id: `appointment-slot-user-${requestId}`,
        text: queryString,
        sender: 'user',
        timestamp: new Date(),
      };
      const botMessageId = `appointment-slot-bot-${requestId}`;
      streamingMessageIdRef.current = botMessageId;

      const botMessage: Message = {
        id: botMessageId,
        text: '',
        sender: 'bot',
        timestamp: new Date(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, botMessage]);
      scrollToEnd();

      sendMessage(queryString);
    },
    [resetStreamedText, scrollToEnd, sendMessage],
  );

  return (
    <ChatListRoot>
      <MessagesScrollArea ref={containerRef} bg="$core.color.neutral.0">
        {!hasStartedChat && !initialPrompt ? (
          <ChatPromptOptions onPromptPress={handlePromptPress} />
        ) : null}

        {messages.map((message, index) => {
          // Show loader on the last user message if API is in progress
          const isLastUserMessage =
            message.sender === 'user' &&
            index === messages.length - 2 &&
            messages[messages.length - 1]?.sender === 'bot' &&
            messages[messages.length - 1]?.isStreaming;

          return (
            <div key={message.id}>
              {renderMessage({ item: message, showLoader: isStreaming && isLastUserMessage })}
            </div>
          );
        })}
      </MessagesScrollArea>

      <FooterContainer>
        <ChatFooter onSend={handleSend} />
      </FooterContainer>

      <CalendarModal
        isVisible={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        onSlotConfirm={handleSlotSelection}
        availabilitySlots={calendarSlots}
      />

      <DynamicFormModal
        isVisible={showUserCaptureForm}
        formConfig={
          dynamicFormConfig ?? {
            title: 'Please enter your details',
            cancelButton: 'Cancel',
            saveButton: 'Save',
            formFields: {},
            initialValues: {},
          }
        }
        onSaveClick={handleDynamicFormSave}
        onClose={handleCloseDynamicFormModal}
      />
    </ChatListRoot>
  );
};

const ChatListRoot = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
});

const MessagesScrollArea = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  paddingTop: '$core.spacing.100',
  paddingBottom: '$core.spacing.100',
  paddingRight: 5,
  paddingLeft: 5,
  // Hide scrollbar for all browsers
  scrollbarWidth: 'none', // Firefox
  '-ms-overflow-style': 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Opera
  },
  props: ({ bg }: { bg?: string }) => ({
    backgroundColor: bg,
  }),
});

const FooterContainer = styled('div', {
  flexShrink: 0,
  paddingTop: '$core.spacing.200',
  width: '100%',
  backgroundColor: '$core.color.neutral.0',
  zIndex: 10,
});

const MessageRow = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '$core.spacing.50',
  paddingBottom: '$core.spacing.50',
});

const UserMessageWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  alignSelf: 'flex-end',
  maxWidth: '80%',
});

const ProviderCardWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '$core.spacing.100',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
});

const ReviewAppointmentWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  marginTop: '$core.spacing.100',
  marginBottom: '$core.spacing.100',
  alignItems: 'flex-end',
  alignSelf: 'stretch',
});

const ProviderSearchResultsWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  marginTop: '$core.spacing.100',
  marginBottom: '$core.spacing.100',
  alignSelf: 'stretch',
});

const BotMessageContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
});

const BotRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  width: '100%',
});

const BotContentWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: 1,
  minWidth: 0,
  marginLeft: 8,
});

const ButtonsWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  animation: 'chatButtonsFadeIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both',
  willChange: 'opacity, transform',

  '@keyframes chatButtonsFadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(12px) scale(0.98)',
    },
    '60%': {
      opacity: 0.6,
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
});

const IconWrapper = styled('div', {
  marginTop: '$core.spacing.150',

  '& > svg': {
    width: 30,
    height: 30,
  },
});

const FeatureCardIconWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '$core.spacing.50',
  borderRadius: '$core.border-radius.lg',
  borderWidth: '$core.border-width.sm',
  borderStyle: 'solid',
  borderColor: '$core.color.neutral.20',
  boxShadow: '0 2px 8px rgba(16, 24, 40, 0.12), 0 1px 2px rgba(16, 24, 40, 0.08)',
  props: ({ bg }: { bg?: string }) => ({
    backgroundColor: bg,
  }),
});
export default ChatMessageList;
